// Import necessary modules and configurations
import { Eventbus } from '../../utils';
import { events } from '../../utils/constants';
import logger from '../../utils/logger';
import axios from 'axios';
import config from '../../config';
import statsApi from './stats.api';
import centreBankingApi from './paymint.api';
import bankfileApi from './bankfile.api';
import transactionsApi from './transactions.api';
import matchApi from './match.api';
import exportApi from './export.api';
import searchApi from './search.api';
import transactionSplitApi from './transactionSplit.api';
import reportApi from './report.api';
import centreApi from './centre.api';
import centreUserApi from './centreUser.api';
import financeApi from './finance.api';
import ToastService from '../toast.service';

import { AuthenticationService } from '../authentication.service';

// Set up the base URL for axios instance
const axiosInstance = axios.create();
axiosInstance.defaults.headers.common['content-type'] = 'application/json';
axiosInstance.defaults.baseURL = config.paymintApiUrl;
axiosInstance.defaults.withCredentials = true;
// Set to keep track of ongoing unsafe requests to prevent duplicate writes
const ongoingRequests = new Set();

function isUnsafeHttpMethod(method) {
    return ['post', 'put', 'patch', 'delete'].includes((method || '').toLowerCase());
}

function serializeRequestPart(value) {
    if (!value) {
        return '';
    }

    if (value instanceof URLSearchParams) {
        return value.toString();
    }

    if (typeof value === 'string') {
        return value;
    }

    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function getRequestKey(request) {
    return [
        request.method,
        request.url,
        serializeRequestPart(request.params),
        serializeRequestPart(request.data)
    ].join('-');
}

async function recoverUnauthorizedRequest(error) {
    return AuthenticationService.recoverUnauthorizedRequest({
        error,
        retryRequest: (requestConfig) => axiosInstance(requestConfig),
        shouldRefreshCsrf: isUnsafeHttpMethod,
        setCsrfHeader: (requestConfig, csrfToken) => {
            requestConfig.headers = {
                ...(requestConfig.headers || {}),
                'X-CSRF-TOKEN': csrfToken
            };
        },
        loginReturnUrl: `${window.location.origin}/login`
    });
}

// Request interceptor to handle request configurations
axiosInstance.interceptors.request.use(async request => {
    const shouldTrackRequest = isUnsafeHttpMethod(request.method);
    const requestKey = shouldTrackRequest ? getRequestKey(request) : null;
    if (shouldTrackRequest && ongoingRequests.has(requestKey)) {
        // If request is ongoing, return the rejected promise
        const error = new axios.Cancel('Duplicate request cancelled');
        return Promise.reject(error);
    }
    if (shouldTrackRequest) {
        ongoingRequests.add(requestKey);
    }
    let isAuthenticated = AuthenticationService.isAuthenticated();
    if (!isAuthenticated) {
        await AuthenticationService.refreshToken();
        isAuthenticated = AuthenticationService.isAuthenticated();
    }
    if (!isAuthenticated) {
        if (shouldTrackRequest) {
            ongoingRequests.delete(requestKey);
        }
        return Promise.reject(new axios.Cancel('User not authenticated'));
    }

    request.withCredentials = true;

    if (isUnsafeHttpMethod(request.method)) {
        const csrfToken = await AuthenticationService.getCsrfToken();
        if (csrfToken) {
            request.headers['X-CSRF-TOKEN'] = csrfToken;
        }
    }

    return request;
}, function (error) {
    // Clean up the ongoing request tracking on error
    if (error.config) {
        const requestKey = getRequestKey(error.config);
        ongoingRequests.delete(requestKey);
    }
    return Promise.reject(error);
});

// Response interceptor to handle response and errors
axiosInstance.interceptors.response.use((response) => {
    const requestKey = getRequestKey(response.config);
    ongoingRequests.delete(requestKey);
    
    // Check if the response has an error status code and reject it
    if (response.status >= 400) {
        const error = new Error(`HTTP Error ${response.status}`);
        error.response = response;
        error.config = response.config;
        return Promise.reject(error);
    }
    
    // Always return the extracted data on success
    return response?.data?.data || response?.data || response;
}, (error) => {
    // Clean up request tracking for any error
    if (error && error.config) {
        const requestKey = getRequestKey(error.config);
        ongoingRequests.delete(requestKey);
    }

    if (error?.response?.status === 401) {
        return recoverUnauthorizedRequest(error).then((recoveredResponse) => {
            if (recoveredResponse) {
                return recoveredResponse;
            }

            return Promise.reject(error);
        });
    }
    
    if (error && error.response) {
        // Emit unauthorized event if status is 401 or 403
        const status = error.response.status;
        if (status === 401 || status === 403) {
            Eventbus.emit(events.HTTP_UNAUTHORIZED, error);
        }
        
        // Extract error details from response
        const responseData = error.response.data || {};
        const title = responseData.statusCode || responseData.title || (status === 401 || status === 403 ? 'Unauthorized' : `Error ${status}`);
        const errors = responseData.errors || responseData.message || 'An error occurred';
        
        // Generate a contextual toastId based on the request
        const url = error.config?.url || '';
        const method = error.config?.method || '';
        const toastId = `api-error-${method}-${url.split('/').pop() || 'unknown'}`;
        
        // Show error toast unless explicitly disabled
        const showToast = error.config?.suppressErrorToast !== true;
        if (showToast && title && errors) {
            ToastService.displayToast({
                type: 'error',
                title: title,
                data: errors,
                toastId: toastId,
                copyToClipboard: true,
                options: { closeOnClick: false }
            });
        }
        
        // Log error centrally for debugging (only once)
        logger.error('API Error:', {
            url: error.config?.url,
            method: error.config?.method,
            status: status,
            title: title,
            errors: errors
        });
        
        // Attach enrichment fields to the original error so callers and tests
        // still receive an Error/axios error with response, stack, and flags.
        try {
            error.title = title;
            error.errors = errors;
            error.toastShown = showToast;
            error.loggedCentrally = true; // Flag to prevent duplicate logging
        } catch (e) {
            // ignore if we cannot assign
        }
    } else if (error && error.code === 'ERR_NETWORK') {
        // Handle network errors (including CORS, timeout, blocked requests)
        // This often happens when backend returns error status without proper CORS headers
        // Common scenario: Backend returns 403/401 but without CORS headers, browser blocks the response
        const url = error.config?.url || '';
        const method = error.config?.method || '';
        const usesCookieSession = error.config?.withCredentials === true;
        const toastId = `api-error-${method}-${url.split('/').pop() || 'unknown'}`;
        
        if (usesCookieSession) {
            Eventbus.emit(events.HTTP_UNAUTHORIZED, error);
        }
        
        const showToast = error.config?.suppressErrorToast !== true;
        if (showToast) {
            // Provide more specific message based on context
            const errorMessage = usesCookieSession 
                ? 'Access denied or authorization failed. You may not have permission for this action.'
                : 'Unable to reach the server. Please check your connection.';
            
            ToastService.displayToast({
                type: 'error',
                title: 'Request Failed',
                data: errorMessage,
                toastId: toastId,
                copyToClipboard: true,
                options: { closeOnClick: false }
            });
        }
        
        logger.error('Network Error (likely CORS blocking 403/401):', {
            url: url,
            method: method,
            message: error.message,
            code: error.code,
            usesCookieSession: usesCookieSession,
            note: 'Backend may be returning 403/401 without proper CORS headers'
        });
    }
    
    return Promise.reject(error);
});

// Function to extract data from response
function getDataResponse(response) {
    return response.data || response;
}

// Function to build request with optional override instance
function buildRequest(overrideInstance) {
    return Promise.resolve(overrideInstance || axiosInstance);
}

// Function to make request and get data response
function makeRequest(req) {
    return req.then(getDataResponse).catch((error) => {
        if (axios.isCancel(error)) {
            return Promise.resolve({ code: 'ERR_CANCELED' });
        }
        // Error logging is handled by the axios interceptor
        // Don't log here to avoid duplicate logs
        return Promise.reject(error);
    });
}

// Main API function to return API methods
function api() {
    return {
        ...centreBankingApi({ makeRequest, buildRequest }),
        ...statsApi({ makeRequest, buildRequest }),
        ...bankfileApi({ makeRequest, buildRequest }),
        ...transactionsApi({ makeRequest, buildRequest }),
        ...exportApi({ makeRequest, buildRequest }),
        ...matchApi({ makeRequest, buildRequest }),
        ...searchApi({ makeRequest, buildRequest }),
        ...transactionSplitApi({ makeRequest, buildRequest }),
        ...centreApi({ makeRequest, buildRequest }),
        ...centreUserApi({ makeRequest, buildRequest }),
        ...reportApi({ makeRequest, buildRequest }),
        ...financeApi({ makeRequest, buildRequest }),
    };
}

export { axiosInstance };

// Export the API function as default
export default api();